import { useSyncExternalStore } from 'react';
import type { AuthTokens, UserProfile } from '@mcgames/contracts';

const REFRESH_KEY = 'mcgames.refresh';

/** `restoring` lasts from app start until the stored refresh token has been tried. */
export type SessionState =
  | { status: 'restoring'; user: null }
  | { status: 'anonymous'; user: null }
  | { status: 'authenticated'; user: UserProfile };

const RESTORING: SessionState = { status: 'restoring', user: null };
const ANONYMOUS: SessionState = { status: 'anonymous', user: null };

let state: SessionState = RESTORING;
/** The access token never leaves memory. */
let accessToken: string | null = null;
const listeners = new Set<() => void>();

function publish(next: SessionState): void {
  state = next;
  for (const listener of listeners) listener();
}

/** The current session outside React; components use `useSession`. */
export function getSessionState(): SessionState {
  return state;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

function storeRefreshToken(token: string | null): void {
  try {
    if (token === null) localStorage.removeItem(REFRESH_KEY);
    else localStorage.setItem(REFRESH_KEY, token);
  } catch {
    // Without storage the session just does not survive a reload.
  }
}

/** Stores a rotated token pair without touching the user. */
export function setTokens(tokens: AuthTokens): void {
  accessToken = tokens.accessToken;
  storeRefreshToken(tokens.refreshToken);
}

export function setSession(tokens: AuthTokens, user: UserProfile): void {
  setTokens(tokens);
  publish({ status: 'authenticated', user });
}

/** Sets the user after tokens were restored (the refresh response carries no profile). */
export function setUser(user: UserProfile): void {
  publish({ status: 'authenticated', user });
}

/**
 * Ends the session. `keepRefreshToken` leaves the stored token for a later restore attempt;
 * `onlyRefreshToken` deletes the stored token only while it is still that one, because another
 * browser tab may have replaced it with a valid one.
 */
export function clearSession(
  options: { keepRefreshToken?: boolean; onlyRefreshToken?: string } = {},
): void {
  accessToken = null;
  const replacedElsewhere =
    options.onlyRefreshToken !== undefined && getRefreshToken() !== options.onlyRefreshToken;
  if (options.keepRefreshToken !== true && !replacedElsewhere) storeRefreshToken(null);
  publish(ANONYMOUS);
}

/** Back to the state at app start; used between tests. */
export function resetSession(): void {
  accessToken = null;
  publish(RESTORING);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useSession(): SessionState {
  return useSyncExternalStore(subscribe, () => state);
}
